/**
 * Support Ticket Detail hook using React Relay
 *
 * This hook provides access to a single support ticket's detailed data
 * through Relay's normalized cache and automatic data management.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLazyLoadQuery, fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type { useSupportTicketDetailRelayQuery as UseSupportTicketDetailRelayQuery } from './__generated__/useSupportTicketDetailRelayQuery.graphql';
import type { useSupportTicketDetailRelayByOldIdQuery as UseSupportTicketDetailRelayByOldIdQuery } from './__generated__/useSupportTicketDetailRelayByOldIdQuery.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

/**
 * Query for a single support ticket by UUID
 */
const SupportTicketDetailQuery = graphql`
  query useSupportTicketDetailRelayQuery($id: ID!) {
    getSupportTicket(id: $id) {
      id
      oldId
      state
      content
      created
      closedDate
      owner
      tags
      type
      subtype
      deviceId
      communicationLog
      notes
      user {
        id
        phone
        fullName
        firstName
        lastName
        email
      }
      hardware {
        id
        oldId
        number
        hardwareType {
          name
        }
      }
      rental {
        id
        oldId
      }
      system {
        id
        oldId
        name
      }
      organizationId
      systemId
      systemOldId
      maintenanceTickets {
        id
        oldId
        dueOn
        completedOn
        canceledOn
        status
        description
      }
    }
  }
`;

/**
 * Query for a single support ticket by oldId
 */
const SupportTicketDetailByOldIdQuery = graphql`
  query useSupportTicketDetailRelayByOldIdQuery(
    $filter: SupportTicketFilterInput!
    $first: Int
  ) {
    listSupportTickets(filter: $filter, first: $first) {
      edges {
        node {
          id
          oldId
          state
          content
          created
          closedDate
          owner
          tags
          type
          subtype
          deviceId
          communicationLog
          notes
          user {
            id
            phone
            fullName
            firstName
            lastName
            email
          }
          hardware {
            id
            oldId
            number
            hardwareType {
              name
            }
          }
          rental {
            id
            oldId
          }
          system {
            id
            oldId
            name
          }
          organizationId
          systemId
          systemOldId
          maintenanceTickets {
            id
            oldId
            dueOn
            completedOn
            canceledOn
            status
            description
          }
        }
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

/**
 * User type from GraphQL support ticket detail
 */
export interface SupportTicketUserDetailGraphQL {
  id: string;
  phone: string;
  fullName?: string | null;
  firstName: string;
  lastName: string;
  email?: string | null;
}

/**
 * Hardware type from GraphQL support ticket detail
 */
export interface SupportTicketHardwareDetailGraphQL {
  id: string;
  oldId?: string | null;
  number?: number | null;
  hardwareType?: {
    name?: string | null;
  } | null;
}

/**
 * Rental type from GraphQL support ticket detail
 */
export interface SupportTicketRentalDetailGraphQL {
  id: string;
  oldId?: string | null;
}

/**
 * System type from GraphQL support ticket detail
 */
export interface SupportTicketSystemDetailGraphQL {
  id: string;
  oldId?: string | null;
  name: string;
}

/**
 * Maintenance Ticket type from GraphQL support ticket detail
 */
export interface SupportTicketMaintenanceTicketGraphQL {
  id: string;
  oldId?: string | null;
  dueOn: string;
  completedOn?: string | null;
  canceledOn?: string | null;
  status?: string | null;
  description: string;
}

/**
 * Full Support Ticket detail type from GraphQL
 */
export interface SupportTicketDetailGraphQL {
  id: string;
  oldId?: string | null;
  state?: number | null;
  content?: string | null;
  created?: string | null;
  closedDate?: string | null;
  owner?: string | null;
  tags?: readonly string[] | null;
  type?: number | null;
  subtype?: string | null;
  deviceId?: string | null;
  communicationLog?: readonly (string | null)[] | null;
  notes?: string | null;
  user?: SupportTicketUserDetailGraphQL | null;
  hardware?: SupportTicketHardwareDetailGraphQL | null;
  rental?: SupportTicketRentalDetailGraphQL | null;
  system?: SupportTicketSystemDetailGraphQL | null;
  organizationId?: string | null;
  systemId?: string | null;
  systemOldId?: string | null;
  maintenanceTickets?: readonly SupportTicketMaintenanceTicketGraphQL[] | null;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching a single support ticket by UUID using Relay
 *
 * This hook uses Relay's useLazyLoadQuery which suspends while loading.
 * Wrap with Suspense boundary.
 *
 * @example
 * function TicketDetail({ ticketId }: { ticketId: string }) {
 *   const { data } = useSupportTicketDetailRelay(ticketId);
 *
 *   return <div>{data?.content}</div>;
 * }
 *
 * // With Suspense
 * <Suspense fallback={<Loading />}>
 *   <TicketDetail ticketId="uuid" />
 * </Suspense>
 */
export function useSupportTicketDetailRelay(ticketId: string) {
  // Initial query - suspends while loading
  const data = useLazyLoadQuery<UseSupportTicketDetailRelayQuery>(
    SupportTicketDetailQuery,
    { id: ticketId }
  );

  return {
    data: data.getSupportTicket as SupportTicketDetailGraphQL | null,
  };
}

// ============================================================================
// React Query-like Interface (No Suspense)
// ============================================================================

/**
 * Hook that provides React Query-like interface for support ticket detail data
 *
 * This hook does NOT use Suspense - it provides isLoading, data, refetch etc.
 * similar to React Query's useQuery.
 *
 * @example
 * const { data, isLoading, refetch } = useSupportTicketDetailRelayQuery(ticketId);
 *
 * if (isLoading) return <Loading />;
 * return <div>{data?.content}</div>;
 */
export function useSupportTicketDetailRelayQuery(
  ticketId: string,
  options?: {
    enabled?: boolean;
  }
) {
  const [data, setData] = useState<SupportTicketDetailGraphQL | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const enabled = options?.enabled !== false;

  useEffect(() => {
    if (!enabled || !ticketId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const result = await fetchQuery<UseSupportTicketDetailRelayQuery>(
          relayEnvironment,
          SupportTicketDetailQuery,
          { id: ticketId }
        ).toPromise();

        if (cancelled) return;

        if (result) {
          setData(result.getSupportTicket as SupportTicketDetailGraphQL | null);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err : new Error('Failed to fetch support ticket')
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ticketId, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
  };
}

/**
 * Hook that fetches support ticket by oldId (legacy ID)
 *
 * This is useful for backwards compatibility when navigating from URLs
 * that still use oldId.
 *
 * @example
 * const { data, isLoading, refetch } = useSupportTicketDetailByOldIdQuery(oldId);
 */
export function useSupportTicketDetailByOldIdQuery(
  oldId: string,
  options?: {
    enabled?: boolean;
  }
) {
  const [data, setData] = useState<SupportTicketDetailGraphQL | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const enabled = options?.enabled !== false;

  useEffect(() => {
    if (!enabled || !oldId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const result = await fetchQuery<UseSupportTicketDetailRelayByOldIdQuery>(
          relayEnvironment,
          SupportTicketDetailByOldIdQuery,
          {
            filter: { oldId: { eq: oldId } },
            first: 1,
          }
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const ticket = result.listSupportTickets?.edges?.[0]?.node;
          setData(ticket as SupportTicketDetailGraphQL | null);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err : new Error('Failed to fetch support ticket')
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, oldId, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
  };
}

/**
 * Smart hook that fetches support ticket by either UUID or oldId
 *
 * Automatically detects UUID format and uses the appropriate query.
 * UUIDs follow the format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
 *
 * @example
 * const { data, isLoading, refetch } = useSupportTicketDetailSmartQuery(problemId);
 */
export function useSupportTicketDetailSmartQuery(
  ticketId: string,
  options?: {
    enabled?: boolean;
  }
) {
  // Check if the ticketId is a UUID
  const isUuid = useMemo(() => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(ticketId);
  }, [ticketId]);

  const uuidResult = useSupportTicketDetailRelayQuery(ticketId, {
    enabled: options?.enabled !== false && isUuid,
  });

  const oldIdResult = useSupportTicketDetailByOldIdQuery(ticketId, {
    enabled: options?.enabled !== false && !isUuid,
  });

  // Return the appropriate result based on the ID type
  if (isUuid) {
    return uuidResult;
  }
  return oldIdResult;
}
